import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, FlaskConical, FileText, BarChart3, Boxes, Users, ExternalLink, Search } from "lucide-react";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";
import Brandmark from "@/components/Brandmark";

const TABS = [
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "protocols", label: "Protocols", icon: Boxes },
  { id: "copy", label: "Mode copy", icon: FileText },
  { id: "simulator", label: "Simulator", icon: FlaskConical },
  { id: "metrics", label: "Metrics", icon: BarChart3 },
];

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#6B6B6B]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full border border-[#EBE9E4] rounded-lg p-2.5 text-[14px] bg-white ss-focus";

function ProtocolEditor({ p, contexts, onSaved }) {
  const [form, setForm] = useState({
    ...p,
    instructions: p.instructions.join("\n"),
    contexts: p.contexts || [],
    incompatible: (p.incompatible || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCtx = (id) =>
    setForm((f) => ({ ...f, contexts: f.contexts.includes(id) ? f.contexts.filter((c) => c !== id) : [...f.contexts, id] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        mode: form.mode, title: form.title, purpose: form.purpose,
        duration_seconds: Number(form.duration_seconds), while_functioning: !!form.while_functioning,
        priority: Number(form.priority), contexts: form.contexts,
        incompatible: form.incompatible.split(",").map((s) => s.trim()).filter(Boolean),
        instructions: form.instructions.split("\n").map((s) => s.trim()).filter(Boolean),
        completion_prompt: form.completion_prompt, active: !!form.active,
      };
      await http.put(`/admin/protocols/${p.id}`, payload);
      toast(`${p.id} saved`);
      onSaved();
    } catch (e) { toast("Save failed"); }
    setBusy(false);
  };

  return (
    <div className="border-t border-[#EBE9E4] p-4 bg-[#FBFAF6]">
      <Field label="Title"><input data-testid={`admin-title-${p.id}`} className={inputCls} value={form.title} onChange={(e) => upd("title", e.target.value)} /></Field>
      <Field label="Purpose"><input className={inputCls} value={form.purpose} onChange={(e) => upd("purpose", e.target.value)} /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Duration (s)">
          <select className={inputCls} value={form.duration_seconds} onChange={(e) => upd("duration_seconds", e.target.value)}>
            {[60, 180, 600, 1200].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Priority"><input type="number" className={inputCls} value={form.priority} onChange={(e) => upd("priority", e.target.value)} /></Field>
        <Field label="While functioning">
          <select className={inputCls} value={form.while_functioning ? "1" : "0"} onChange={(e) => upd("while_functioning", e.target.value === "1")}>
            <option value="0">No</option><option value="1">Yes</option>
          </select>
        </Field>
      </div>
      <Field label="Compatible demand contexts">
        <div className="flex flex-wrap gap-2">
          {contexts.map((c) => (
            <button key={c.id} type="button" onClick={() => toggleCtx(c.id)}
              className={`rounded-full px-3 py-1.5 text-[12px] border ${form.contexts.includes(c.id) ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#EBE9E4] bg-white text-[#474747]"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Incompatible / unsafe contexts (comma-separated, e.g. driving)">
        <input className={inputCls} value={form.incompatible} onChange={(e) => upd("incompatible", e.target.value)} />
      </Field>
      <Field label="Instructions (one step per line)">
        <textarea rows={5} className={inputCls} value={form.instructions} onChange={(e) => upd("instructions", e.target.value)} />
      </Field>
      <Field label="Completion prompt"><input className={inputCls} value={form.completion_prompt} onChange={(e) => upd("completion_prompt", e.target.value)} /></Field>
      <label className="flex items-center gap-2 text-[13px] mb-4">
        <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} className="accent-[#C9A227]" /> Active
      </label>
      <button data-testid={`admin-save-${p.id}`} onClick={save} disabled={busy}
        className="bg-[#C9A227] text-[#1A1A1A] rounded-full px-5 py-2.5 text-[14px] font-semibold ss-tap ss-focus">
        {busy ? "Saving…" : "Save protocol"}
      </button>
    </div>
  );
}

export default function Admin() {
  const { user, authReady, isAdmin, logout } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("subscribers");
  const [protocols, setProtocols] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [copy, setCopy] = useState([]);
  const [open, setOpen] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [events, setEvents] = useState(null);
  const [subs, setSubs] = useState(null);
  const [biz, setBiz] = useState(null);
  const [subFilter, setSubFilter] = useState("all");
  const [subQuery, setSubQuery] = useState("");
  const [sim, setSim] = useState({ mode: "CLEAR", demand_context: "make_decision", available_time: "t180", result: null });

  useEffect(() => {
    if (authReady && !isAdmin) nav("/login");
  }, [authReady, isAdmin, nav]);

  const loadProtocols = useCallback(() => {
    http.get("/admin/protocols").then(({ data }) => { setProtocols(data.protocols); setContexts(data.contexts); }).catch(() => {});
  }, []);
  const loadCopy = useCallback(() => {
    http.get("/admin/mode-copy").then(({ data }) => setCopy(data.mode_copy)).catch(() => {});
  }, []);

  useEffect(() => { if (isAdmin) { loadProtocols(); loadCopy(); } }, [isAdmin, loadProtocols, loadCopy]);
  useEffect(() => { if (tab === "metrics" && isAdmin) http.get("/admin/metrics").then(({ data }) => setMetrics(data)).catch(() => {}); }, [tab, isAdmin]);
  useEffect(() => { if (tab === "metrics" && isAdmin) http.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {}); }, [tab, isAdmin]);

  const loadSubs = useCallback(() => {
    const params = new URLSearchParams();
    if (subFilter !== "all") params.set("plan", subFilter);
    if (subQuery.trim()) params.set("q", subQuery.trim());
    http.get(`/admin/subscribers?${params.toString()}`).then(({ data }) => setSubs(data.subscribers)).catch(() => setSubs([]));
    http.get("/admin/business").then(({ data }) => setBiz(data)).catch(() => {});
  }, [subFilter, subQuery]);

  useEffect(() => { if (tab === "subscribers" && isAdmin) loadSubs(); }, [tab, isAdmin, subFilter, loadSubs]);

  const grantPlus = async (id) => {
    try { await http.post(`/admin/subscribers/${id}/grant-plus`); toast("Plus granted (comped)"); loadSubs(); }
    catch (e) { toast("Could not grant Plus"); }
  };
  const revokePlus = async (id) => {
    if (!window.confirm("Revoke Plus for this account? If they have a paid Stripe subscription it will be cancelled.")) return;
    try { await http.post(`/admin/subscribers/${id}/revoke-plus`); toast("Plus revoked"); loadSubs(); }
    catch (e) { toast("Could not revoke Plus"); }
  };

  const runSim = async () => {
    try {
      const { data } = await http.post("/admin/simulate", {
        mode: sim.mode, demand_context: sim.demand_context, available_time: sim.available_time });
      setSim((s) => ({ ...s, result: data }));
    } catch (e) { toast("Simulation failed"); }
  };

  const saveCopy = async (mode, body) => {
    try { await http.put(`/admin/mode-copy/${mode}`, body); toast(`${mode} copy saved`); loadCopy(); }
    catch (e) { toast("Save failed"); }
  };

  if (!authReady) return <div className="p-10 text-[#6B6B6B]">Loading…</div>;
  if (!isAdmin) return null;

  const times = [["t60", "60s"], ["t180", "3 min"], ["t600", "10 min"], ["t1200", "20 min"], ["cannot_stop", "Cannot stop"]];
  const modes = ["CLEAR", "SETTLE", "RESTORE", "PROTECT", "RECONNECT", "MOBILIZE"];

  return (
    <div className="min-h-screen bg-[#F3F1EA]">
      <header className="bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Brandmark size={22} tone="light" wordmark={false} />
            <div>
              <div className="font-serif-display text-2xl">Signal State ID · Admin</div>
              <div className="text-[12px] text-[#B9B4A7]">{user?.email}</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/"); }}
            className="inline-flex items-center gap-2 text-[13px] border border-[#3a3a3a] rounded-full px-4 py-2 ss-focus">
            <LogOut size={15} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5">
        <nav className="flex gap-2 py-4 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[13px] border ${tab === id ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white border-[#EBE9E4] text-[#474747]"}`}>
              <Icon size={15} strokeWidth={1.5} /> {label}
            </button>
          ))}
        </nav>

        {tab === "subscribers" && (
          <div className="pb-16" data-testid="admin-subscribers">
            {/* Business metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["MRR", biz ? `$${(biz.mrr ?? 0).toFixed(2)}` : "—"],
                ["ARR", biz ? `$${(biz.arr ?? 0).toFixed(2)}` : "—"],
                ["Plus", biz ? (biz.plus ?? 0) : "—"],
                ["Trialing", biz ? (biz.trialing ?? 0) : "—"],
                ["Free", biz ? (biz.free ?? 0) : "—"],
                ["Conversion", biz ? `${biz.conversion_pct ?? 0}%` : "—"],
                ["Comped", biz ? (biz.comped ?? 0) : "—"],
                ["Ending", biz ? (biz.canceling ?? 0) : "—"],
              ].map(([l, v]) => (
                <div key={l} className="bg-white border border-[#EBE9E4] rounded-xl p-4" data-testid={`biz-${l.toLowerCase()}`}>
                  <div className="text-[12px] text-[#6B6B6B]">{l}</div>
                  <div className="font-serif-display text-2xl">{v}</div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a9a94]" />
                <input
                  data-testid="admin-subs-search"
                  value={subQuery}
                  onChange={(e) => setSubQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadSubs()}
                  placeholder="Search email…"
                  className="w-full pl-9 pr-3 py-2 text-[13px] bg-white border border-[#EBE9E4] rounded-full ss-focus" />
              </div>
              {["all", "plus", "free"].map((f) => (
                <button key={f} data-testid={`admin-subs-filter-${f}`} onClick={() => setSubFilter(f)}
                  className={`rounded-full px-3.5 py-2 text-[12.5px] border ${subFilter === f ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white border-[#EBE9E4] text-[#474747]"}`}>
                  {f === "all" ? "All" : f === "plus" ? "Plus" : "Free"}
                </button>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-[#6B6B6B]">
              Account &amp; billing metadata only. Individual check-in content is never shown here — privacy is core to Signal State ID.
            </p>

            {/* Table */}
            {subs === null ? (
              <p className="mt-6 text-[#6B6B6B]">Loading…</p>
            ) : subs.length === 0 ? (
              <p className="mt-6 text-[13px] text-[#6B6B6B]">No accounts match.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {subs.map((s) => (
                  <div key={s.id} className="bg-white border border-[#EBE9E4] rounded-xl p-4" data-testid="admin-sub-row">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] font-medium truncate">{s.email}</span>
                          {s.role === "admin" && <span className="text-[10px] uppercase tracking-wide rounded-full bg-[#1A1A1A] text-white px-2 py-0.5">Admin</span>}
                          <span className={`text-[10.5px] uppercase tracking-wide rounded-full px-2 py-0.5 ${s.plan === "plus" ? "bg-[#F7F2D7] text-[#8a6d10]" : "bg-[#F0EEE8] text-[#6B6B6B]"}`} data-testid="sub-plan-badge">
                            {s.plan}
                          </span>
                          {s.plan === "plus" && s.plan_status && (
                            <span className="text-[10.5px] rounded-full bg-[#F0EEE8] text-[#474747] px-2 py-0.5">
                              {s.plan_comped ? "comped" : s.plan_status}
                              {s.plan_interval ? ` · ${s.plan_interval === "year" ? "annual" : "monthly"}` : ""}
                            </span>
                          )}
                          {s.plan_cancel_at_period_end && (
                            <span className="text-[10.5px] rounded-full bg-[#FBE9D6] text-[#9a5b1e] px-2 py-0.5">ending</span>
                          )}
                        </div>
                        <div className="mt-1.5 text-[12px] text-[#6B6B6B] flex flex-wrap gap-x-4 gap-y-0.5">
                          <span>{s.checkins} check-ins</span>
                          {s.last_active && <span>last active {new Date(s.last_active).toLocaleDateString()}</span>}
                          {s.created_at && <span>joined {new Date(s.created_at).toLocaleDateString()}</span>}
                          {s.plan === "plus" && s.plan_trial_end && s.plan_status === "trialing" && (
                            <span>trial ends {new Date(s.plan_trial_end).toLocaleDateString()}</span>
                          )}
                          {s.plan === "plus" && !s.plan_comped && s.plan_renews_at && s.plan_status !== "trialing" && (
                            <span>{s.plan_cancel_at_period_end ? "ends" : "renews"} {new Date(s.plan_renews_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        {s.plan === "plus" ? (
                          <button data-testid={`sub-revoke-${s.id}`} onClick={() => revokePlus(s.id)}
                            className="text-[12px] border border-[#e0b4b4] text-[#a13a3a] rounded-full px-3 py-1.5 ss-focus">
                            Revoke Plus
                          </button>
                        ) : (
                          <button data-testid={`sub-grant-${s.id}`} onClick={() => grantPlus(s.id)}
                            className="text-[12px] border border-[#1A1A1A] rounded-full px-3 py-1.5 ss-focus">
                            Grant Plus
                          </button>
                        )}
                        {s.stripe_customer_id && (
                          <a data-testid={`sub-stripe-${s.id}`}
                            href={`https://dashboard.stripe.com/test/customers/${s.stripe_customer_id}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11.5px] text-[#8a6d10] ss-focus">
                            Stripe <ExternalLink size={12} strokeWidth={1.8} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "protocols" && (
          <div className="pb-16" data-testid="admin-protocols">
            <p className="text-[13px] text-[#6B6B6B] mb-3">{protocols.length} protocols · edits apply immediately, no code required.</p>
            <div className="space-y-2">
              {protocols.map((p) => (
                <div key={p.id} className="bg-white border border-[#EBE9E4] rounded-xl overflow-hidden">
                  <button data-testid={`admin-protocol-${p.id}`} onClick={() => setOpen(open === p.id ? null : p.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left ss-focus">
                    <span className="flex items-center gap-3">
                      <span className="font-mono-ss text-xs text-[#C9A227]">{p.id}</span>
                      <span className="text-[14px] font-medium">{p.title}</span>
                      {!p.active && <span className="text-[11px] text-[#a33]">inactive</span>}
                    </span>
                    <span className="text-[11px] text-[#6B6B6B]">{p.mode} · {p.duration_seconds}s</span>
                  </button>
                  {open === p.id && <ProtocolEditor p={p} contexts={contexts} onSaved={loadProtocols} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "copy" && (
          <div className="pb-16 space-y-3" data-testid="admin-copy">
            {copy.map((c) => <CopyEditor key={c.mode} c={c} onSave={saveCopy} />)}
          </div>
        )}

        {tab === "simulator" && (
          <div className="pb-16" data-testid="admin-simulator">
            <div className="bg-white border border-[#EBE9E4] rounded-xl p-4 grid grid-cols-3 gap-3">
              <Field label="Mode">
                <select className={inputCls} value={sim.mode} onChange={(e) => setSim({ ...sim, mode: e.target.value })}>
                  {modes.map((m) => <option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Demand context">
                <select className={inputCls} value={sim.demand_context} onChange={(e) => setSim({ ...sim, demand_context: e.target.value })}>
                  {contexts.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Available time">
                <select className={inputCls} value={sim.available_time} onChange={(e) => setSim({ ...sim, available_time: e.target.value })}>
                  {times.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
            <button data-testid="admin-simulate-run" onClick={runSim}
              className="mt-3 bg-[#1A1A1A] text-white rounded-full px-5 py-2.5 text-[14px] font-medium ss-focus">Run simulation</button>
            {sim.result && (
              <div className="mt-4 bg-white border border-[#EBE9E4] rounded-xl p-4" data-testid="admin-sim-result">
                <div className="text-[13px] text-[#6B6B6B]">Selected</div>
                <div className="font-serif-display text-2xl mt-1">{sim.result.chosen?.title}</div>
                <div className="text-[12px] font-mono-ss text-[#C9A227]">{sim.result.chosen?.id} · {sim.result.chosen?.duration_label}</div>
                <div className="mt-4 text-[13px] font-semibold">Why this was selected</div>
                <ol className="mt-1 list-decimal list-inside text-[13px] text-[#474747] space-y-1">
                  {sim.result.reason.map((r, i) => <li key={i}>{r}</li>)}
                </ol>
                <div className="mt-4 text-[13px] font-semibold">Top ranked candidates</div>
                <table className="mt-1 w-full text-[13px]">
                  <tbody>
                    {sim.result.ranked.map((r) => (
                      <tr key={r.id} className="border-t border-[#EBE9E4]">
                        <td className="py-1.5 font-mono-ss text-[#6B6B6B]">{r.id}</td>
                        <td className="py-1.5">{r.title}</td>
                        <td className="py-1.5 text-right">{r.context_match ? "ctx✓ " : ""}score {r.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "metrics" && (
          <div className="pb-16" data-testid="admin-metrics">
            {!metrics ? <p className="text-[#6B6B6B]">Loading…</p> : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[["Check-ins", metrics.total_checkins], ["Completed", metrics.completed], ["Baselines", metrics.baselines], ["Users", metrics.users]].map(([l, v]) => (
                    <div key={l} className="bg-white border border-[#EBE9E4] rounded-xl p-4">
                      <div className="text-[12px] text-[#6B6B6B]">{l}</div>
                      <div className="font-serif-display text-3xl">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-[#EBE9E4] rounded-xl p-4">
                  <div className="text-[13px] font-semibold mb-2">Mode distribution</div>
                  {Object.entries(metrics.mode_distribution).map(([m, n]) => (
                    <div key={m} className="flex justify-between text-[13px] py-1"><span>{m}</span><span>{n}</span></div>
                  ))}
                  {Object.keys(metrics.mode_distribution).length === 0 && <p className="text-[13px] text-[#6B6B6B]">No data yet.</p>}
                </div>
                <div className="bg-white border border-[#EBE9E4] rounded-xl p-4">
                  <div className="text-[13px] font-semibold mb-2">Protocols flagged (not possible / no help / made harder)</div>
                  {metrics.flagged_protocols.length ? metrics.flagged_protocols.map((f) => (
                    <div key={f.id} className="flex justify-between text-[13px] py-1"><span>{f.id} · {f.title}</span><span>{f.count}</span></div>
                  )) : <p className="text-[13px] text-[#6B6B6B]">None flagged.</p>}
                </div>
                <div className="bg-white border border-[#EBE9E4] rounded-xl p-4" data-testid="admin-events">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[13px] font-semibold">Events · last 7 days</div>
                    {events && events.completion_rate != null && (
                      <span className="text-[12px] text-[#6B6B6B]">Check-in completion {events.completion_rate}%</span>
                    )}
                  </div>
                  {!events ? <p className="text-[13px] text-[#6B6B6B]">Loading…</p> :
                    events.events.length ? events.events.map((e) => (
                      <div key={e.name} className="flex justify-between text-[13px] py-1"><span>{e.name}</span><span>{e.count}</span></div>
                    )) : <p className="text-[13px] text-[#6B6B6B]">No events yet.</p>}
                  <p className="mt-2 text-[11.5px] text-[#9a9a94]">Aggregate counts only — no check-in content or free text is ever stored.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CopyEditor({ c, onSave }) {
  const [f, setF] = useState(c);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="bg-white border border-[#EBE9E4] rounded-xl p-4">
      <div className="font-serif-display text-2xl mb-3">{c.mode}</div>
      <Field label="Purpose"><input className={inputCls} value={f.purpose || ""} onChange={(e) => upd("purpose", e.target.value)} /></Field>
      <Field label="Observation"><textarea rows={3} className={inputCls} value={f.observation || ""} onChange={(e) => upd("observation", e.target.value)} /></Field>
      <Field label="Validation"><textarea rows={2} className={inputCls} value={f.validation || ""} onChange={(e) => upd("validation", e.target.value)} /></Field>
      <Field label="Permission"><textarea rows={2} className={inputCls} value={f.permission || ""} onChange={(e) => upd("permission", e.target.value)} /></Field>
      <button data-testid={`admin-copy-save-${c.mode}`} onClick={() => onSave(c.mode, { purpose: f.purpose, observation: f.observation, validation: f.validation, permission: f.permission })}
        className="bg-[#C9A227] text-[#1A1A1A] rounded-full px-5 py-2.5 text-[14px] font-semibold ss-focus">Save {c.mode} copy</button>
    </div>
  );
}
